import { Button } from '@zond/ui';
import { Apple, Monitor } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

export const DOWNLOAD_LINKS = {
  MAC_ARM: 'https://storage.googleapis.com/klarops-desktop-releases/releases/Klarops_aarch64.dmg',
  MAC_INTEL: 'https://storage.googleapis.com/klarops-desktop-releases/releases/Klarops_x64.dmg',
  WINDOWS: 'https://storage.googleapis.com/klarops-desktop-releases/releases/Klarops_x64-setup.exe',
};

// Detecting user's OS
export const detectUserOS = () => {
  try {
    const platform = window.navigator.platform.toLowerCase();
    if (platform.includes('win')) return 'windows';
    if (platform.includes('mac')) {
      // Determining processor type on Mac
      const userAgent = window.navigator.userAgent.toLowerCase();
      return userAgent.includes('arm') ? 'mac_arm' : 'mac_intel';
    }
    return null;
  } catch {
    return null;
  }
};

export interface ClientDownloadOptionsProps {
  className?: string;
  onDownloadClick?: () => void;
}

export const ClientDownloadOptions: React.FC<ClientDownloadOptionsProps> = ({
  className = '',
  onDownloadClick,
}) => {
  const userOS = detectUserOS();
  const { t } = useTranslation('downloads');

  return (
    <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${className}`}>
      {/* Mac ARM */}
      <Button
        variant={userOS === 'mac_arm' ? 'default' : 'outline'}
        size="lg"
        className={`p-6 h-auto flex flex-col items-center gap-2 shadow-lg transition-all hover:scale-105 ${
          userOS === 'mac_arm'
            ? 'hover:shadow-primary/25 bg-gradient-to-r from-primary to-primary/90'
            : 'hover:shadow-border/25'
        }`}
        onClick={() => {
          if (onDownloadClick) onDownloadClick();
          window.location.href = DOWNLOAD_LINKS.MAC_ARM;
        }}
      >
        <Apple className="w-8 h-8" />
        <div className="flex flex-col gap-1">
          <span className="font-semibold">{t('platforms.macos.name')}</span>
          <span className="text-xs text-muted-foreground">{t('platforms.macos.appleSilicon')}</span>
        </div>
      </Button>

      {/* Mac Intel */}
      <Button
        variant={userOS === 'mac_intel' ? 'default' : 'outline'}
        size="lg"
        className={`p-6 h-auto flex flex-col items-center gap-2 shadow-lg transition-all hover:scale-105 ${
          userOS === 'mac_intel'
            ? 'hover:shadow-primary/25 bg-gradient-to-r from-primary to-primary/90'
            : 'hover:shadow-border/25'
        }`}
        onClick={() => {
          if (onDownloadClick) onDownloadClick();
          window.location.href = DOWNLOAD_LINKS.MAC_INTEL;
        }}
      >
        <Apple className="w-8 h-8" />
        <div className="flex flex-col gap-1">
          <span className="font-semibold">{t('platforms.macos.name')}</span>
          <span className="text-xs text-muted-foreground">{t('platforms.macos.intel')}</span>
        </div>
      </Button>

      {/* Windows */}
      <Button
        variant={userOS === 'windows' ? 'default' : 'outline'}
        size="lg"
        className={`p-6 h-auto flex flex-col items-center gap-2 shadow-lg transition-all hover:scale-105 ${
          userOS === 'windows'
            ? 'hover:shadow-primary/25 bg-gradient-to-r from-primary to-primary/90'
            : 'hover:shadow-border/25'
        }`}
        onClick={() => {
          if (onDownloadClick) onDownloadClick();
          window.location.href = DOWNLOAD_LINKS.WINDOWS;
        }}
      >
        <Monitor className="w-8 h-8" />
        <div className="flex flex-col gap-1">
          <span className="font-semibold">{t('platforms.windows.name')}</span>
          <span className="text-xs text-muted-foreground">{t('platforms.windows.arch')}</span>
        </div>
      </Button>
    </div>
  );
};
